import { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { useToast } from '@chakra-ui/react'
import api from '../services/api'
import { desinscreverNoLogout } from '../services/webPush'

interface User {
  id: number
  name: string
  email: string
  profilePic: string | null
  rolesId: number
  role?: {
    name: string
    permissions: { name: string; code: string }[]
  }
}

interface AuthContextData {
  user: User | null
  permissions: string[]
  isAuthenticated: boolean
  isLoading: boolean
  canManageSystem: boolean
  login: (email: string, password: string) => Promise<boolean>
  logout: () => void
}

const AuthContext = createContext<AuthContextData>({} as AuthContextData)

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [permissions, setPermissions] = useState<string[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const toast = useToast()

  const isAuthenticated = !!user
  const canManageSystem = permissions.includes('manage_support_system')

  useEffect(() => {
    const loadStoredAuth = async () => {
      const token = localStorage.getItem('wedrop_support_token')
      const storedUser = localStorage.getItem('wedrop_support_user')

      if (token && storedUser) {
        try {
          // Validate token and refresh user data
          const response = await api.get('/me')
          setUser(response.data)
          setPermissions(response.data.permissions || [])
        } catch (error) {
          localStorage.removeItem('wedrop_support_token')
          localStorage.removeItem('wedrop_support_user')
        }
      }
      setIsLoading(false)
    }

    loadStoredAuth()
  }, [])

  const login = async (email: string, password: string): Promise<boolean> => {
    setIsLoading(true)
    try {
      const response = await api.post('/auth/login', { email, password })

      if (response.data.access_token) {
        localStorage.setItem('wedrop_support_token', response.data.access_token)
        localStorage.setItem('wedrop_support_user', JSON.stringify(response.data.user))

        setUser(response.data.user)
        setPermissions(response.data.permissions || [])

        toast({
          title: 'Login realizado com sucesso',
          status: 'success',
          duration: 3000,
          position: 'top-right',
        })

        return true
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Erro ao fazer login'
      toast({
        title: 'Erro no login',
        description: errorMessage,
        status: 'error',
        duration: 5000,
        position: 'top-right',
      })
    } finally {
      setIsLoading(false)
    }
    return false
  }

  const logout = () => {
    // Computador compartilhado: o próximo a entrar não pode receber os
    // chamados da conta anterior. Não segura o logout.
    void desinscreverNoLogout()
    localStorage.removeItem('wedrop_support_token')
    localStorage.removeItem('wedrop_support_user')
    setUser(null)
    setPermissions([])
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        permissions,
        isAuthenticated,
        isLoading,
        canManageSystem,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}
